import React from "react";
import {
  FileText,
  Truck,
  Package,
  ClipboardList,
  Receipt,
  Users,
  Map,
  Warehouse,
  Folder,
  BarChart3,
  ArrowUpRight,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { timeAgo } from "@/lib/timeago";
import { formatApiError } from "@/lib/errors";

// Keep in sync with COMMON_ICONS in AdminApps.jsx
const ICON_MAP = {
  "file-text": FileText,
  truck: Truck,
  package: Package,
  "clipboard-list": ClipboardList,
  receipt: Receipt,
  users: Users,
  map: Map,
  warehouse: Warehouse,
  folder: Folder,
  "bar-chart-3": BarChart3,
};

export async function openAppNewTab(app) {
  try {
    const { data } = await api.post(`/sso/launch/${app.id}`);
    window.open(data.launch_url, "_blank", "noopener");
  } catch (e) {
    toast.error(formatApiError(e) || `Couldn't launch ${app.name}`);
  }
}

export default function AppTile({ app, onClick }) {
  const Icon = ICON_MAP[app.icon] || FileText;
  const last = timeAgo(app.last_launched_at);

  return (
    <div
      data-testid={`app-tile-${app.id}`}
      className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-cargodash-primary-500/50 hover:shadow"
    >
      <button
        type="button"
        onClick={() => onClick?.(app)}
        className="absolute inset-0 z-0"
        aria-label={`Launch ${app.name}`}
        data-testid={`app-tile-launch-${app.id}`}
      />

      <div className="relative z-10 pointer-events-none flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cargodash-primary-50 text-cargodash-primary-700">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-semibold text-slate-900">
              {app.name}
            </span>
            {app.status === "active" && (
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-cargodash-primary-500" />
            )}
          </div>
          <div className="mt-0.5 truncate text-xs text-slate-500">
            {app.description || app.url.replace(/^https?:\/\//, "")}
          </div>
        </div>
        <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-cargodash-primary-600" />
      </div>

      <div className="relative z-10 mt-4 flex items-center justify-between gap-3">
        <div className="text-[11px] text-slate-500" data-testid={`app-tile-last-${app.id}`}>
          {last ? `Last used: ${last}` : "Never used"}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openAppNewTab(app);
          }}
          title="Open in new tab"
          data-testid={`app-tile-newtab-${app.id}`}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
        >
          <ExternalLink className="h-3 w-3" />
          New tab
        </button>
      </div>
    </div>
  );
}
